import queryString from "query-string";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import FileDownloadLineIcon from "remixicon-react/FileDownloadLineIcon";

import { getPartPaymentList, uploadPartPaymentFile } from "../../../core/actionCreators/partPaymentActionCreators";
import { downloadPartPaymentService } from "../../../core/services/partPaymentService";
import { GET_PART_PAYMENT_DEFAULT_SIZE, PART_PAYMENT_PAGE_HEADERS } from "../../../utils/constants";
import { convertJsonToCsv, removeEmptyStringKeys } from "../../../utils/helper";
import CommonPagination from "../CommonPagination/CommonPagination";
import Loader from "../Loader/Loader";
import PartPaymentListItem from "./PartPaymentListItem";

const EMPTY_FILTERS = {
    productId: "",
    active: "",
}

const PartPaymentList = () => {
    const dispatch = useDispatch()

    const { partPaymentData, isLoading } = useSelector((s: any) => s.partPaymentStore)

    const { user } = useSelector((s: any) => s.authStore)

    const [file, setFile] = useState("")
    const [isDownloading, setIsDownloading] = useState(false)

    // `filters` is what the user is typing; `searchCriteria` is what has actually
    // been searched for. Only Search / Clear / pagination move one into the other.
    const [filters, setFilters] = useState({ ...EMPTY_FILTERS } as any)
    const [searchCriteria, setSearchCriteria] = useState({
        ...EMPTY_FILTERS,
        page: 0,
        size: GET_PART_PAYMENT_DEFAULT_SIZE,
    } as any)

    useEffect(() => {
        dispatch(getPartPaymentList(queryString.stringify(removeEmptyStringKeys(searchCriteria)), null));
    }, [searchCriteria, dispatch]);

    const onSearch = () => {
        setSearchCriteria({ ...searchCriteria, ...filters, page: 0 })
    }

    const onFilterKeyDown = (e: any) => {
        if (e.key === "Enter") onSearch()
    }

    // CommonPagination counts pages from 1, the API counts them from 0.
    const onPageChange = (pageNumber: any) => {
        setSearchCriteria({ ...searchCriteria, page: Math.max(0, Number(pageNumber) - 1) })
    }

    const hasActiveFilters = Object.keys(EMPTY_FILTERS).some((key) => !!filters[key] || !!searchCriteria[key])

    const clearFilters = () => {
        setFilters({ ...EMPTY_FILTERS })
        setSearchCriteria({ ...searchCriteria, ...EMPTY_FILTERS, page: 0 })
    }

    // Exports every row matching the filters currently applied to the listing —
    // pagination is deliberately dropped so the file is not just the visible page.
    const downloadFilteredData = async () => {
        setIsDownloading(true)
        try {
            const { page, size, ...filterCriteria } = searchCriteria
            const response: any = await downloadPartPaymentService(
                queryString.stringify(removeEmptyStringKeys(filterCriteria))
            )

            // A failed download still resolves — the error body arrives as a JSON blob.
            if (response.data?.type?.includes("json")) {
                const errorText = await response.data.text()
                toast.error(JSON.parse(errorText)?.statusDescription || "Failed to download file.")
                return
            }

            const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv" }))
            const link = document.createElement("a")
            link.href = url
            link.setAttribute("download", `part-payment-${new Date().toISOString().slice(0, 10)}.csv`)
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
            toast.success("Downloaded successfully.")
        }
        catch (err: any) {
            console.log("downloadFilteredData: exceptionError: ", err)
            toast.error("Failed to download file.")
        }
        finally {
            setIsDownloading(false)
        }
    }

    const fileHandler = (e: any) => {
        if (e.target.files && e.target.files[0]) {
            let file = e.target.files[0];
            setFile(file)
        }
    }

    const bulkUploadHandler = () => {
        if (!file) {
            toast.error("Please select file.")
            return
        }
        const formData = new FormData()
        formData.append("file", file)
        dispatch(uploadPartPaymentFile({
            param: `email=${user?.email || ""}`,
            data: formData
        }, (d: any) => {
            toast.success("Uploaded successfully.")
            // Re-fetch the listing so the uploaded rows show up.
            setSearchCriteria({ ...searchCriteria, page: 0 })
        }))
    }

    const downloadSampleFile = () => {
        const data = [
            { productId: 'MSNE9J83N6609D', active: "TRUE" }
        ];
        convertJsonToCsv(data, 'sample.csv')
    }

    return (
        <>
            <div className="p-4 flex">
                <p className="text-xl font-bold text-[#43425D] w-1/5">PART PAYMENT</p>
                <div className="flex items-center w-4/5 ml-auto justify-end">
                    <button
                        type="button"
                        onClick={downloadSampleFile}
                        title="Download a sample CSV showing the columns required for bulk upload"
                        className="flex items-center gap-1 mr-4 text-xs font-medium text-blue-500 hover:text-blue-700 hover:underline whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 rounded"
                    >
                        <FileDownloadLineIcon size={14} />
                        Download Sample File
                    </button>
                    <input onChange={(e) => fileHandler(e)} type="file" accept=".csv" title="Select the filled CSV to bulk upload" className="border border-gray-300 py-2 rounded focus: outline-none px-1.5 text-xs w-1/4" />
                    <button onClick={bulkUploadHandler} className="h-9 py-2 px-4 bg-blue-500 text-white font-semibold rounded shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 ml-5">Bulk Upload</button>
                </div>
            </div>
            <div className="mt-2.5 bg-white px-4 py-3 flex flex-wrap items-end gap-x-4 gap-y-3">
                <div className="w-[220px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Msn</label>
                    <input type="text" placeholder="Search MSN" value={filters.productId} onChange={(e) => setFilters({ ...filters, productId: e.target.value })} onKeyDown={onFilterKeyDown} className="border border-gray-300 h-8 rounded-sm focus:outline-none focus:border-blue-400 px-1.5 text-xs w-full" />
                </div>
                <div className="w-[160px]">
                    <label className="block text-[11px] text-gray-600 mb-1">Part Payment</label>
                    <select value={filters.active} onChange={(e) => setFilters({ ...filters, active: e.target.value })} className="bg-white border border-gray-300 h-8 w-full focus:outline-none focus:border-blue-400 px-1.5 rounded-sm text-xs">
                        <option value=''>All</option>
                        <option value='true'>Enabled</option>
                        <option value='false'>Disabled</option>
                    </select>
                </div>
                <button
                    onClick={onSearch}
                    className="h-8 px-5 rounded text-xs font-semibold bg-blue-500 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75"
                >
                    Search
                </button>
                <button
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                    className="h-8 px-4 rounded text-xs font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Clear Filters
                </button>
                <button
                    onClick={downloadFilteredData}
                    disabled={isDownloading}
                    title="Download all rows matching the applied filters"
                    className="h-8 px-4 ml-auto rounded text-xs font-medium border border-blue-500 text-blue-600 bg-white hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {isDownloading ? "Downloading..." : "Download CSV"}
                </button>
            </div>
            <div className="mt-2.5 campaign-list bg-white mb-[54px]">
                <table className="w-full">
                    <thead className="bg-[#343434]">
                        <tr>
                            {PART_PAYMENT_PAGE_HEADERS.map((header: string) => (
                                <th key={header} className="px-4 py-3 text-left text-xs font-semibold text-white">{header}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? <Loader /> : <PartPaymentListItem data={partPaymentData?.products || []} />}
                    </tbody>
                </table>
            </div>
            {partPaymentData?.products && <CommonPagination data={partPaymentData} PAGE_SIZE={GET_PART_PAYMENT_DEFAULT_SIZE} onPageChange={onPageChange} />}
        </>
    )
}
export default PartPaymentList;
